import {useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {Bookmark, Plus} from 'lucide-react';
import {Button, Card} from '@tinct/web-components';
import {CreateAudienceModal} from '@tinct/web-feature-companies';
import {LayoutHeader} from '@tinct/web-layout';
import {Collection} from '@tinct/web-collection';
import {useGetAudiences, useGetCampaignById} from '@tinct/core-api';
import {useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {useAudienceSelectionColumns} from './hooks';
import {ReadAudience, JobStatus} from "@tinct/core-api-models";
import {useFunnel} from "@tinct/web-funnel";
import {CampaignCreationFunnelState} from "../../campaign-creation-funnel/campaign-creation-funnel";
import {AudienceSummary} from "./audience-types";

export type AudienceStepState = {
  selectedAudienceId: string | null;
  selectedAudience: AudienceSummary | null;
  currentAudience: AudienceSummary | null;
  isPending: boolean;
  lastImportJobStatus: JobStatus | null;
};

const toAudienceSummary = (
  audience: ReadAudience | null | undefined,
): AudienceSummary | null =>
  audience?.id
    ? {
        id: audience.id,
        name: audience.name ?? '',
        companiesCount: audience.statistics?.leadsCount ?? 0,
      }
    : null;

type Step4AudienceProps = {
  onStateChange?: (state: Partial<AudienceStepState>) => void;
};

export const Step4Audience = ({onStateChange}: Step4AudienceProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const workspaceId = useGetCurrentWorkspaceId();
  const {pathname} = useLocation();
  const columns = useAudienceSelectionColumns();
  const { state } = useFunnel<CampaignCreationFunnelState>();
  const campaignId = state.campaign.id;

  // Fetch campaign to get existing audience
  const { data: campaign } = useGetCampaignById({
    id: campaignId || '',
    fields: {
      audience: {
        fields: {
          id: null,
          name: null,
          lastImportJob: null,
          statistics: {
            fields: {
              leadsCount: null,
            },
          },
        },
      },
    },
  }, {
    enabled: !!campaignId,
  });

  // Initialize from campaign's existing audience
  const initialAudienceId = campaign?.audience?.id;
  const [selectedAudience, setSelectedAudience] = useState<ReadAudience | null>(null);

  // Initialize with existing audience when campaign loads (only once)
  useEffect(() => {
    if (initialAudienceId && !selectedAudience) {
      setSelectedAudience(campaign?.audience as ReadAudience);
    }
  }, [initialAudienceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of selection changes
  useEffect(() => {
    onStateChange?.({
      selectedAudienceId: selectedAudience?.id ?? null,
      selectedAudience: toAudienceSummary(selectedAudience),
      currentAudience: toAudienceSummary(campaign?.audience),
      lastImportJobStatus: selectedAudience?.lastImportJob?.status ?? null,
    });
  }, [selectedAudience, campaign?.audience, onStateChange]);

  // Stable callback for selection changes
  const handleSelectionChange = useCallback((audience: ReadAudience) => {
    setSelectedAudience(audience ?? null);
  }, []);

  // Pre-fetch to check if audiences exist for conditional rendering
  const { data: audiencesData, isLoading } = useGetAudiences({
    staticParams: {
      organizationId: workspaceId,
    },
    fields: {
      id: null,
    },
    offset: 0,
    limit: 1,
  });

  const hasAudiences = (audiencesData?.pagination.count ?? 0) > 0;

  const EmptyStateCard = (
    <Card className="flex flex-col items-center justify-center py-16 rounded-2xl">
      <div className="flex flex-col gap-6 items-center max-w-[308px]">
        <div className="flex flex-col gap-4 items-center w-full">
          <div className="flex items-center justify-center size-10 bg-muted rounded-lg">
            <Bookmark className="size-6 text-foreground" />
          </div>
          <div className="flex flex-col gap-2 items-center text-center w-full">
            <p className="text-lg font-medium text-foreground">
              No audience yet
            </p>
            <p className="text-sm text-muted-foreground">
              Add your first audience
            </p>
          </div>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="size-4" />
          Create an audience
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="flex flex-col w-full items-center pt-8 pb-24 px-16">
      <div className="flex flex-col gap-8 w-full max-w-[896px]">
        {/* Page Header */}
        <LayoutHeader
          pageTitle="Define audience"
          pageDescription="Select the companies you want to target"
        />

        {/* Audiences List Block */}
        <div className="flex flex-col gap-2.5 w-full">
          {!isLoading && hasAudiences && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setIsModalOpen(true)}>
                <Plus className="size-4" />
                Create an audience
              </Button>
            </div>
          )}

          {isLoading ? (
            <Card className="flex flex-col items-center justify-center py-16 rounded-2xl">
              <div className="text-muted-foreground">Loading...</div>
            </Card>
          ) : !hasAudiences ? (
            EmptyStateCard
          ) : (
            <Collection
              columns={columns}
              useQueryFn={useGetAudiences}
              staticParams={{
                organizationId: workspaceId,
              }}
              queryFields={{
                id: null,
                name: null,
                lastImportJob: null,
                statistics: {
                  fields: {
                    leadsCount: null,
                    campaignsCount: null,
                  },
                },
                createdAt: null,
              }}
              selection={selectedAudience}
              onSelectionChange={handleSelectionChange}
              emptyState={EmptyStateCard}
              canBeSelected
              singleSelection={true}
              refetchInterval={5000}
            />
          )}
        </div>
      </div>

      <CreateAudienceModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        completedUrl={pathname}
        onSuccess={(audience) => {
          setSelectedAudience(audience);
          setIsModalOpen(false);
        }}
      />
    </div>
  );
};
